import { SimpleFormSelect, SimpleFormSelectOption } from "../form/SimpleFormSelect";
import SVG from "react-inlinesvg";
import { SimpleFormCheck } from "../form/SimpleFormCheck";
import SimpleFormTagsInput from "../form/SimpleFormTagsInput";
import { SimpleFormControl } from "../form/SimpleFormControl";
import { useIntl } from "react-intl";
import { SimpleFormTextarea } from "../form/SimpleFormTextarea";
import { IconButton } from "..";
import { useField } from "formik";
import { useEffect, useState } from "react";
import { Order } from "src/app/types/orders/order";
import { includes } from "lodash";
import moment from "moment";
import { UserRole } from "src/app/transports/user";
import { toAbsoluteUrl } from "src/_metronic/helpers";

export interface IUpsertDraftOrderFormBody {
    contentTypeOptions?: Array<SimpleFormSelectOption>;
    writingTypeOptions?: Array<SimpleFormSelectOption>;
    orderTypeOptions?: Array<SimpleFormSelectOption>;
    moneySiteOptions?: Array<SimpleFormSelectOption>;
    rdOptions?: Array<SimpleFormSelectOption>;
    thematicOptions?: Array<SimpleFormSelectOption>;
    fieldArrayName?: string;
    checkIfRDIsUsed(moneySiteId: number, currentRDID: number): Promise<boolean>;
    currentUserId?: number | UserRole.ROLE_ADMIN;
    formData?: Order;
    indexOfTheForm?: number;
    handleDataForm?: (index: number | null, action: "DELETE" | "ADD") => void;
}

const UpsertDraftOrderFormBody = (props: IUpsertDraftOrderFormBody) => {
    const {
        contentTypeOptions = [],
        writingTypeOptions = [],
        orderTypeOptions = [],
        indexOfTheForm = 0,
        moneySiteOptions = [],
        checkIfRDIsUsed,
        formData,
        rdOptions = [],
        thematicOptions = [],
        fieldArrayName = "",
        currentUserId,
        handleDataForm = () => {},
    } = props;
    const { formatMessage } = useIntl();
    const [moneySiteIdField, moneySiteIdMeta] = useField(`${fieldArrayName}moneySiteId`);
    const [, , thematicIdHelper] = useField(`${fieldArrayName}thematicId`);
    const [rdId, , rdIdHelper] = useField(`${fieldArrayName}rdId`);
    const [contentType] = useField(`${fieldArrayName}contentType`);
    const [optiField, , optiHelper] = useField(`${fieldArrayName}opti`);
    const [links0, , links0Helpers] = useField(`${fieldArrayName}links.0`);
    const [links1, , links1helpers] = useField(`${fieldArrayName}links.1`);
    const [commentForLinker] = useField(`${fieldArrayName}commentForLinker`);
    const [, , publishedAtHelpers] = useField(`${fieldArrayName}publishedAt`);
    const [priority] = useField(`${fieldArrayName}priority`);
    const [keywords, , keywordsHelper] = useField(`${fieldArrayName}keywords`);
    const publishAllowedData = moment().add(7, "d").format("YYYY-MM-DD");
    const [rdIsUsed, setRdIsUsed] = useState<boolean>(false);

    useEffect(() => {
        if (links0.value?.url === "" && links0.value?.anchor === "") {
            links0Helpers.setValue(links1.value);
            links1helpers.setValue({ url: "", anchor: "" });
        }
    }, [links0.value?.url, links0.value?.anchor]);

    useEffect(() => {
        if (keywords.value?.length > 0 && !optiField.value) {
            optiHelper.setValue(true);
        } else if (keywords.value?.length === 0 && optiField.value) {
            optiHelper.setValue(false);
        }

        // add word (cooc) to secund keyword
        if (keywords.value?.length >= 2 && !includes(keywords.value[1], "(cooc)")) {
            const auxKeyword: string[] = keywords.value;
            auxKeyword[1] = `${keywords.value[1]} (cooc)`;
            keywordsHelper.setValue(auxKeyword);
        }
        rdId.value === moneySiteIdField.value;

        if (keywords.value?.length > 0 && !optiField.value) {
            optiHelper.setValue(true);
        } else if (keywords.value?.length === 0 && optiField.value) {
            optiHelper.setValue(false);
        }

        // if we remove 1st keyword, remove (cooc) from the 1st key word
        if (keywords.value?.length > 0 && includes(keywords.value[0], "(cooc)")) {
            const auxKeyword: string[] = keywords.value;
            auxKeyword[0] = keywords.value[0]?.replace("(cooc)", "");
            keywordsHelper.setValue(auxKeyword);
        }
    }, [keywords.value]);

    useEffect(() => {
        if (!moneySiteOptions || !moneySiteIdField.value) {
            if (rdId.value) rdIdHelper.setValue(null);
            return;
        }
        const currentMoneySite = moneySiteOptions?.find((el) => el.value == moneySiteIdField.value);
        const currentRDId = rdId.value;

        if (moneySiteIdMeta.touched) rdIdHelper.setValue(currentRDId);

        thematicIdHelper.setValue(currentMoneySite?.thematicId);
    }, [moneySiteIdField.value]);

    useEffect(() => {
        if (!rdId.value || rdId.value === moneySiteIdField.value) return;

        checkIfRDIsUsed(moneySiteIdField.value, rdId.value).then((el) => {
            setRdIsUsed(el);
        });
    }, [rdId.value]);

    useEffect(() => {
        if (priority.value) {
            publishedAtHelpers.setValue(null);
        }
    }, [priority.value]);

    return (
        <div className="upsertDraftOrderFormBodyWrapper">
            <div className="upsertDraftOrderFormBodyGrid">
                <div>
                    <SimpleFormSelect
                        name={`${fieldArrayName}moneySiteId`}
                        values={moneySiteOptions}
                        placeholder={formatMessage({ id: "common.field.moneySite" })}
                    />
                    <div className="sideForm">
                        <SimpleFormControl
                            name={`${fieldArrayName}wordCount`}
                            placeholder={formatMessage({ id: "common.field.wordsCount" })}
                            type="number"
                            min="300"
                            step={1}
                        />
                        <SimpleFormControl
                            name={`${fieldArrayName}orderPrice`}
                            placeholder={formatMessage({ id: "common.field.orderPrice" })}
                            type="number"
                            max="300"
                            step={0.01}
                        />
                    </div>
                </div>
                <div>
                    <SimpleFormSelect
                        className="contentWrapper"
                        name={`${fieldArrayName}contentType`}
                        values={contentTypeOptions}
                        placeholder={formatMessage({ id: "common.field.contentType" })}
                    />
                    <SimpleFormSelect
                        name={`${fieldArrayName}writingType`}
                        values={writingTypeOptions}
                        placeholder={formatMessage({ id: "common.field.writingType" })}
                    />
                    <SimpleFormSelect
                        name={`${fieldArrayName}orderType`}
                        values={orderTypeOptions}
                        placeholder={formatMessage({ id: "common.field.orderType" })}
                    />
                </div>
                <div>
                    {currentUserId === UserRole.ROLE_ADMIN && (
                        <SimpleFormCheck
                            name={`${fieldArrayName}writerNeeded`}
                            label={formatMessage({ id: "common.field.writerNeeded" })}
                        />
                    )}

                    <SimpleFormCheck
                        name={`${fieldArrayName}approveNeeded`}
                        label={formatMessage({ id: "common.field.approveNeeded" })}
                    />
                    <SimpleFormCheck
                        name={`${fieldArrayName}priority`}
                        label={formatMessage({ id: "common.field.priority" })}
                    />
                </div>
                <div>
                    <SimpleFormSelect
                        name={`${fieldArrayName}thematicId`}
                        values={thematicOptions}
                        placeholder={formatMessage({ id: "common.field.thematic" })}
                    />
                    <SimpleFormTagsInput
                        name={`${fieldArrayName}keywords`}
                        sorted={false}
                        tagPlaceholder={formatMessage({
                            id: "common.field.placeholder.addKeyword",
                        })}
                    />
                </div>
                <div>
                    <SimpleFormControl
                        placeholder={formatMessage({ id: "common.field.title" })}
                        name={`${fieldArrayName}title`}
                    />

                    <div>
                        <div className="sideForm">
                            <div className="flex-grow-1">
                                <SimpleFormControl
                                    name={`${fieldArrayName}links.0.url`}
                                    placeholder={formatMessage({ id: "common.field.url" })}
                                />
                            </div>
                            <SimpleFormControl
                                disabled={!links0.value}
                                name={`${fieldArrayName}links.0.anchor`}
                                placeholder={formatMessage({ id: "common.field.anchor" })}
                            />
                        </div>
                        {(links0.value?.url !== "" || links0.value?.anchor !== "") &&
                            (links0.value?.url != undefined ||
                                links0.value?.anchor != undefined) && (
                                <div className="sideForm">
                                    <div className="flex-grow-1">
                                        <SimpleFormControl
                                            name={`${fieldArrayName}links.1.url`}
                                            placeholder={formatMessage({ id: "common.field.url" })}
                                        />
                                    </div>
                                    <SimpleFormControl
                                        disabled={!links0.value}
                                        name={`${fieldArrayName}links.1.anchor`}
                                        placeholder={formatMessage({ id: "common.field.anchor" })}
                                    />
                                </div>
                            )}
                    </div>
                </div>

                <div className="commentForWriterWrapper">
                    <SimpleFormTextarea
                        name={`${fieldArrayName}commentForWriter`}
                        className="textarea"
                        rows={3}
                        placeholder={formatMessage({ id: "common.field.commentForWriter" })}
                        type="textarea"
                    />
                </div>

                <div>
                    {(rdId.value == "" || rdId.value == undefined) && (
                        <SimpleFormControl
                            name={`${fieldArrayName}commentForLinker`}
                            placeholder={formatMessage({ id: "common.field.commentForLinker" })}
                        />
                    )}

                    {(commentForLinker.value == "" ||
                        commentForLinker.value == undefined ||
                        contentType.value === "LANDING") && (
                        <>
                            <SimpleFormSelect
                                name={`${fieldArrayName}rdId`}
                                disabled={rdOptions.length === 1}
                                values={rdOptions}
                                placeholder={formatMessage({ id: "common.field.rd" })}
                            />
                            {!!rdIsUsed && (
                                <div className="dangerText">
                                    <SVG
                                        className="pulse-animation"
                                        src={toAbsoluteUrl(
                                            "/media/icons/duotune/general/gen044.svg"
                                        )}
                                    />
                                    {formatMessage({ id: "error.rd.used" })}
                                </div>
                            )}
                        </>
                    )}
                    {!priority.value && (
                        <SimpleFormControl
                            min={publishAllowedData}
                            type="date"
                            name={`${fieldArrayName}publishedAt`}
                            placeholder={formatMessage({ id: "common.field.publishedAt" })}
                        />
                    )}
                </div>

                <div className="actionsWrapper">
                    {formData?.id && (
                        <SimpleFormCheck type="checkbox" name={`${fieldArrayName}draft`} />
                    )}
                    <span className="svg-icon svg-icon-2x svg-icon-primary">
                        <IconButton
                            onClick={() => {
                                handleDataForm(null, "ADD");
                            }}
                            width={32}
                            src="/media/icons/duotune/general/gen041.svg"
                        />
                    </span>
                    <span className="svg-icon svg-icon-2x svg-icon-success">
                        <IconButton
                            width={32}
                            onClick={() => {
                                handleDataForm(indexOfTheForm, "ADD");
                            }}
                            src="/media/icons/duotune/general/gen028.svg"
                        />
                    </span>
                    {indexOfTheForm != 0 && (
                        <span className="svg-icon svg-icon-2x svg-icon-danger">
                            <IconButton
                                width={32}
                                onClick={() => {
                                    handleDataForm(indexOfTheForm, "DELETE");
                                }}
                                src="/media/icons/duotune/general/gen027.svg"
                            />
                        </span>
                    )}
                </div>
            </div>
            <hr></hr>
        </div>
    );
};
export default UpsertDraftOrderFormBody;
